import React, { useState, useMemo, useEffect, useRef } from 'react'
import { AutoSizer, Grid } from 'react-virtualized'
import { Typography, Flex, Pagination, PaginationProps } from 'antd'
import { Icon } from '@/shared/ui/Icon/Icon'
import {
  AccountsGridList,
  AccountsGridVirtualContainer,
  PaginationContainer,
  PagerButton,
} from '../styled'
import { SmallInput } from '@/shared/ui/components/Input'
import { AccountCard } from './AccountCard'
import { useBreakpoint } from '@/app/hooks/useBreakpoint'
import useDebounce from '@/app/hooks/useDebounce'
import type { Account } from './AccountCard'
import { SYSTEM_TYPE, useSystemStore } from '@/app/store/systemStore'
import { getItemsPerPage } from '../lib/getItemsPerPage'

const { Text } = Typography

const VIRTUALIZATION_THRESHOLD = 1000
const CARD_HEIGHT = 200
const GAP = 32

const itemRender: PaginationProps['itemRender'] = (
  _,
  type,
  originalElement
) => {
  if (type === 'prev') {
    return (
      <PagerButton gap={4} align="center">
        <Icon name="pager_left" size={16} />
        Предыдущая
      </PagerButton>
    )
  }
  if (type === 'next') {
    return (
      <PagerButton gap={4} align="center">
        Следующая
        <Icon name="pager_right" size={16} />
      </PagerButton>
    )
  }
  return originalElement
}

export interface AccountsGridProps {
  accounts: Account[]
  selectedAccounts: string[]
  onAccountSelect: (accountId: string) => void
  isLoading?: boolean
  searchLabel?: string
  searchPlaceholder?: string
  currentPage?: number
}

export const AccountsGrid: React.FC<AccountsGridProps> = ({
  accounts,
  selectedAccounts,
  onAccountSelect,
  isLoading = false,
  searchLabel = 'Учетная запись',
  searchPlaceholder = 'Поиск по клиенту',
  currentPage: initialPage = 1,
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(initialPage)
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  })
  const gridRef = useRef<Grid>(null)
  const { isXl, isLg, isMd, isSm, isXs } = useBreakpoint()
  const isMobile = isXs || isSm
  const debouncedWindowSize = useDebounce(windowSize, 100)

  const ITEMS_PER_PAGE = useMemo(
    () => getItemsPerPage({ isXs, isSm, isMd, isLg, isXl }),
    [isXs, isSm, isMd, isLg, isXl]
  )

  const GRID_COLUMNS = useMemo(() => {
    if (isXs || isSm || isMd) return 1
    if (isLg) return 2
    if (isXl) return 3
    return 4
  }, [isXs, isSm, isMd, isLg, isXl])

  const recalculateGrid = () => {
    gridRef.current?.recomputeGridSize()
  }

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight })
      recalculateGrid()
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    recalculateGrid()
  }, [debouncedWindowSize, GRID_COLUMNS])

  // Сравниваем с реальными предыдущими значениями, а не "первый ли это вызов" —
  // StrictMode в dev вызывает эффект монтирования дважды, флаг-тумблер это не переживает.
  const lastPageResetDeps = useRef({ searchQuery, ITEMS_PER_PAGE })

  useEffect(() => {
    const prev = lastPageResetDeps.current
    if (
      prev.searchQuery === searchQuery &&
      prev.ITEMS_PER_PAGE === ITEMS_PER_PAGE
    ) {
      return
    }
    lastPageResetDeps.current = { searchQuery, ITEMS_PER_PAGE }
    setCurrentPage(1)
  }, [searchQuery, ITEMS_PER_PAGE])

  const filteredAccounts = useMemo(() => {
    if (!searchQuery.trim()) return accounts
    const q = searchQuery.toLowerCase()
    return accounts.filter(
      (account) =>
        account.title.toLowerCase().includes(q) ||
        account.username?.toLowerCase().includes(q) ||
        account.path.toLowerCase().includes(q)
    )
  }, [accounts, searchQuery])

  const paginatedAccounts = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
    return filteredAccounts.slice(startIndex, startIndex + ITEMS_PER_PAGE)
  }, [filteredAccounts, currentPage, ITEMS_PER_PAGE])

  const totalPages = Math.ceil(filteredAccounts.length / ITEMS_PER_PAGE)
  const gridRowCount = Math.ceil(paginatedAccounts.length / GRID_COLUMNS)
  const shouldUseVirtualization =
    filteredAccounts.length > VIRTUALIZATION_THRESHOLD

  const { selectedSystem } = useSystemStore()
  const isGlonassoft = selectedSystem === SYSTEM_TYPE.GLONASSOFT

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Icon name="spinner" size={24} spin />
        <Text type="secondary" style={{ display: 'block', marginTop: '8px' }}>
          {isGlonassoft
            ? 'Загрузка клиентов...'
            : 'Загрузка учетных записей...'}
        </Text>
      </div>
    )
  }

  const buildSearchQueryNotFoundMsg = (searchQuery: string) => {
    const prefix = isGlonassoft ? 'Клиенты' : 'Учетные записи'
    return searchQuery
      ? prefix + ' по вашему запросу не найдены'
      : prefix + ' не найдены'
  }

  return (
    <Flex vertical gap={32}>
      <SmallInput
        label={searchLabel}
        placeholder={searchPlaceholder}
        prefix={<Icon name="input_search" size={16} />}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={{ maxWidth: '436px' }}
      />

      {accounts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Text type="secondary">Учетные записи не найдены</Text>
        </div>
      ) : filteredAccounts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Text type="secondary">
            {buildSearchQueryNotFoundMsg(searchQuery)}
          </Text>
        </div>
      ) : (
        <>
          {shouldUseVirtualization ? (
            <AccountsGridVirtualContainer>
              <AutoSizer>
                {({ height, width }) => {
                  const columnWidth =
                    (width - GAP * (GRID_COLUMNS - 1)) / GRID_COLUMNS
                  return (
                    <Grid
                      ref={gridRef}
                      cellRenderer={({ columnIndex, rowIndex, key, style }) => {
                        const account =
                          paginatedAccounts[
                            rowIndex * GRID_COLUMNS + columnIndex
                          ]
                        if (!account) return null
                        return (
                          <div
                            key={key}
                            style={{ ...style, width: columnWidth }}
                          >
                            <AccountCard
                              account={account}
                              isSelected={selectedAccounts.includes(account.id)}
                              onClick={onAccountSelect}
                            />
                          </div>
                        )
                      }}
                      columnCount={GRID_COLUMNS}
                      columnWidth={columnWidth + GAP}
                      rowCount={gridRowCount}
                      rowHeight={CARD_HEIGHT + GAP}
                      height={height}
                      width={width}
                    />
                  )
                }}
              </AutoSizer>
            </AccountsGridVirtualContainer>
          ) : (
            <AccountsGridList>
              {paginatedAccounts.map((account) => (
                <AccountCard
                  key={account.id}
                  account={account}
                  isSelected={selectedAccounts.includes(account.id)}
                  onClick={onAccountSelect}
                />
              ))}
            </AccountsGridList>
          )}

          {totalPages > 1 && (
            <PaginationContainer justify={!isMobile ? 'end' : 'center'}>
              <Pagination
                current={currentPage}
                total={filteredAccounts.length}
                pageSize={ITEMS_PER_PAGE}
                onChange={setCurrentPage}
                showSizeChanger={false}
                itemRender={itemRender}
              />
            </PaginationContainer>
          )}
        </>
      )}
    </Flex>
  )
}
